import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { ActivatedRoute, Router } from '@angular/router';
import { AssetStatuses } from 'app/models/control-center/asset.model';
import { AssetTypes } from 'app/models/control-center/assetType.model';
import { AutocompleteComponent } from 'app/components/autocomplete/autocomplete.component';
import { HttpService } from 'app/services/http.service';
import { environment } from 'environments/environment';
import { LogsType } from 'app/components/sendLogsModal/sendLogsModal.component';
import { Utils } from 'app/utils';
import { ActivityComponent } from 'app/components/activity/activity.component';
import { catchError, distinctUntilChanged, from, map, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Person } from 'app/models/control-center/person.model';

const removeSpaces = control => {
    const replaced = control?.value?.replace(/[^0-9a-z:]/ig, '');
    if (replaced && replaced !== control.value) {
        control.setValue(replaced);
    }
    return null;
};

@Component({
  selector: 'app-asset',
  templateUrl: './asset.component.html',
  styleUrls: ['./asset.component.scss'],
})
export class AssetComponent implements OnInit {
    private readonly _route = inject(ActivatedRoute);
    private readonly _httpService = inject(HttpService);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);

    @ViewChild(ActivityComponent) activityComponent: ActivityComponent;
    @ViewChild('subAssetAutocomplete') subAssetAutocomplete: AutocompleteComponent | undefined;

  serialNumberControl = new FormControl('', [Validators.required, removeSpaces]);
  assetModelControl = new FormControl('', [Validators.required]);
  warehouseControl = new FormControl('', [Validators.required]);
  personControl = new FormControl('', [Validators.required]);
  statusControl = new FormControl('', [Validators.required]);
  warehouseLocationControl = new FormControl('', [Validators.required]);
  siteControl = new FormControl('', [Validators.required]);

  loading = true;
  assetId: number;
  asset: any;
  ogAsset: any;
  events: any = [];
  notes = '';
  posting = false;
  assetModels: any = [];
  warehouses: any = [];
  allAssets: any = [];
  sites: any = [];
  assetStatuses = AssetStatuses;
  assetTypes = AssetTypes;

  // This can take forever to load, so we've moved it outside of the ngOnInit.
  people = toSignal<Person[] | null>(from(this._httpService.get('people')), { initialValue: null });

  subAssets: any = [];
  subAsset: any;
  showAddButton = false;
  subAssetsChanged = false;
  permissions;
  readonly waltAssetType = environment.waltAssetType.toString();
  readonly speakerMicAssetType = environment.speakerMicAssetType.toString();
  logsType = LogsType;
  logsModalOpen: boolean = false;

  isWalt = false;
  waltWifiDisabledDate: Date;
  isWaltWifiDisabled = false;

  isEnableDisableWaltWifisModalOpen = false;
  isEnablingDisablingWaltWifis = false;

  isDeleteSavedWifiModalOpen = false;
  isDeletingSavedWifis = false;

  isInvalidateWaltCredentialsModalOpen = false;
  isInvalidatingWaltCredentials = false;


  formatPersonDisplay = (person: any) => Utils.formatPersonDisplay(person);

  ngOnInit() {
    this._route.paramMap.pipe(
      map(params => Number(params.get('id'))),
      distinctUntilChanged(),
      switchMap(assetId => this.loadAsset(assetId)),
      catchError((error: unknown) => {
        console.error(error);
        alert(`There was an error loading the asset: ${error}`);
        return [];
      }),
      takeUntilDestroyed(this._destroyRef),
    ).subscribe();
  }

  private async loadAsset(assetId: number) {
    this.loading = true;
    this.assetId = assetId;
    if (this.assetId === 0) {

      this.asset = {
        assetTypeId: Number.parseInt(this._route.snapshot.queryParams['assetTypeId'], 10),
        created: '',
        status: null,
      };
      this.assetModelControl.setValue('');
      this.warehouseControl.setValue('');
      this.siteControl.setValue('');
      this.serialNumberControl.setValue('');
      this.serialNumberControl.enable();
    } else {
      this.asset = await this._httpService.get('inventory/assets/' + this.assetId);
      this.assetModelControl.setValue(this.asset.assetModelId);
      this.warehouseControl.setValue(this.asset.warehouseId);
      this.siteControl.setValue(this.asset.siteId);
      this.serialNumberControl.setValue(this.asset.serialNumber);
      this.serialNumberControl.disable();
      this.updateWaltWifiInfo();
    }
    this.ogAsset = { ...this.asset };

    await Promise.all([
        (async () => this.assetModels = await this._httpService.get(`inventory/assetTypes/${this.asset.assetTypeId}/assetModels`))(),
        (async () => this.warehouses = await this._httpService.get('inventory/warehouses'))(),
        (async () => this.subAssets = await this._httpService.get(`inventory/assets/${this.assetId}/subAssets`))(),
        (async () => this.allAssets = await this._httpService.get(`inventory/assets/${this.assetId}/availableSubAssets`))(),
        (async () => this.permissions = await this._httpService.get('people/authenticated/permissions'))(),
        (async () => this.sites = await this._httpService.get('companies/sites'))(),
    ]);

    this.loading = false;
  }

  async save() {
      try {
          const assetId = this.assetId;
          this.loading = true;

          if (this.asset.cost && typeof this.asset.cost !== 'string') {
              this.asset.cost = this.asset.cost.toFixed(2);
          }

          if (this.subAssetsChanged) {
              await this._httpService.delete(`inventory/assets/${assetId}/subAssets`);
              for (let i = 0; i < this.subAssets.length; i++) {
                  this.subAssets[i].parentAssetId = assetId;
                  this.subAssets[i].subAssetId = this.subAssets[i].subAssetId ? this.subAssets[i].subAssetId : this.subAssets[i].id;
              }
              await this._httpService.post('inventory/subAssets', this.subAssets);
          }

          if (assetId === 0) await this._httpService.post('inventory/assets', this.asset);
          else await this._httpService.put('inventory/assets', this.asset);
          this._router.navigateByUrl('/inventory/assetTypes/assets/' + this.asset.assetTypeId);
      } catch (err) {
          console.error('Error saving asset', err);
          alert(`There was an error saving the asset: ${err?.error?.message}`);
      } finally {
          this.loading = false;
      }
  }

  async postNotes() {
    this.posting = true;
    await this._httpService.post('inventory/assets/events', { assetId: this.assetId, notes: this.notes, event: 'Note' });
    this.events = await this._httpService.get('inventory/assets/' + this.assetId + '/events');
    this.notes = '';
    this.posting = false;
  }

  subAssetChanges(events) {
    this.subAssetsChanged = true;
    this.subAssets = events;
  }

  goToSubAsset(id) {
    window.location.href = '/inventory/assets/' + id;
  }

  rowClick(id: any) {
    this._router.navigateByUrl('/inventory/assets/' + id);
  }

  validateNumber(event: any) {
    let testing = '';
    try {
      testing = this.asset.cost.toString();
    } catch { /*Ignore*/ }
    const test_split = testing.split('.');
    if (event.code.includes('Digit') && test_split.length > 1 && test_split[1].length > 2) {
      // this.asset.cost = this.asset.cost.toFixed(2);
      this.asset.cost = test_split[0] + '.' + test_split[1].slice(0, 2);
    }
  }

  showLogsModal() {
    this.logsModalOpen = true;
  }

  async handleEnableDisableWaltWifiSubmitted() {
    this.asset = await this._httpService.get('inventory/assets/' + this.assetId);
    this.updateWaltWifiInfo();
    await this.activityComponent.init();
  }

  private updateWaltWifiInfo() {
    this.waltWifiDisabledDate = this.asset?.wifiDisabledUntil ? new Date(`${this.asset.wifiDisabledUntil}Z`) : null;
    this.isWaltWifiDisabled = !!this.waltWifiDisabledDate && this.waltWifiDisabledDate.valueOf() > Date.now();
  }
}
